/**
 * Working Capital Simulator + CMA Data.
 *
 * User picks horizon (3/6/9/12 months), tweaks assumptions or accepts the
 * historical defaults, and gets a month-wise projection of AR/AP/Inventory/
 * Cash + peak WC gap + recommended sanction limit + a downloadable CMA PDF.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Wallet, RefreshCw, FileDown, TrendingUp, TrendingDown } from "lucide-react";
import { downloadBlob } from "@/lib/download";

const fmtINR = (v) => "₹ " + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtShort = (v) => {
  const a = Math.abs(Number(v || 0));
  if (a >= 1e7) return "₹" + (v / 1e7).toFixed(2) + "Cr";
  if (a >= 1e5) return "₹" + (v / 1e5).toFixed(2) + "L";
  return "₹" + Number(v || 0).toLocaleString("en-IN");
};

export default function WorkingCapitalSimulator() {
  const [horizon, setHorizon] = useState(12);
  const [useDefaults, setUseDefaults] = useState(true);
  const [historical, setHistorical] = useState(null);
  const [assumptions, setAssumptions] = useState({
    revenue_growth_pct: 8,
    gross_margin_pct: null,
    ar_days: null, ap_days: null, inv_days: null,
    opex_pct_of_revenue: null,
    starting_bank: null,
  });
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/working-capital/historical", { params: { lookback_months: 12 } })
      .then(r => setHistorical(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load history"));
  }, []);

  const runSim = async () => {
    setLoading(true);
    try {
      const body = { horizon_months: horizon };
      if (!useDefaults) {
        body.assumptions = Object.fromEntries(
          Object.entries(assumptions).filter(([, v]) => v !== null && v !== "")
        );
      } else {
        body.assumptions = { revenue_growth_pct: assumptions.revenue_growth_pct };
      }
      const r = await api.post("/reports/working-capital/simulate", body);
      setSim(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    }
    setLoading(false);
  };
  useEffect(() => { runSim(); }, [horizon]);  // eslint-disable-line

  const downloadCMA = async () => {
    setPdfBusy(true);
    try {
      const body = { history_years: 4, projection_years: 3 };
      if (!useDefaults) {
        body.assumptions = Object.fromEntries(
          Object.entries(assumptions).filter(([, v]) => v !== null && v !== "")
        );
      }
      await downloadBlob("/reports/cma-data.pdf", "CMA_Data.pdf", { method: "POST", data: body });
      toast.success("CMA data PDF downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "PDF failed"); }
    setPdfBusy(false);
  };

  const set = (patch) => setAssumptions(s => ({ ...s, ...patch }));

  const maxBar = useMemo(() => {
    if (!sim?.projections) return 1;
    return Math.max(...sim.projections.map(r => Math.max(r.ar, r.ap, r.inventory, Math.abs(r.working_capital_gap))), 1);
  }, [sim]);

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="wc-simulator-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Working Capital · Simulator</div>
          <h1 className="text-3xl font-bold mt-1 font-heading">Working Capital Simulator</h1>
          <div className="text-sm text-muted-foreground mt-1 max-w-3xl leading-relaxed">
            Pull historical AR/AP/Inventory days from the last 12 months, tweak
            growth or margin assumptions, and see month-wise projected working
            capital · recommended sanction limit · lowest projected bank balance.
            Download the banker-ready CMA data PDF for your working-capital
            facility application (4y history + 3y projections + ratios + MPBF).
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Horizon</Label>
          <Select value={String(horizon)} onValueChange={(v) => setHorizon(parseInt(v, 10))}>
            <SelectTrigger className="w-32 h-8 text-sm" data-testid="wc-horizon"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 months</SelectItem>
              <SelectItem value="6">6 months</SelectItem>
              <SelectItem value="9">9 months</SelectItem>
              <SelectItem value="12">12 months</SelectItem>
              <SelectItem value="24">24 months</SelectItem>
              <SelectItem value="36">36 months</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={downloadCMA} disabled={pdfBusy}
            className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5 ml-2" data-testid="wc-cma-pdf">
            {pdfBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            CMA Data PDF
          </Button>
        </div>
      </div>

      {/* Historical rollup */}
      {historical && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="wc-history">
          {[
            ["Revenue (12M)", historical.revenue_total, "text-emerald-600"],
            ["Gross Margin", `${historical.gross_margin_pct}%`, "text-blue-600"],
            ["AR Days", historical.ar_days, "text-amber-600"],
            ["AP Days", historical.ap_days, "text-purple-600"],
            ["Inventory Days", historical.inv_days, "text-orange-600"],
            ["Cash Cycle", historical.cash_conversion_cycle, "text-red-600"],
          ].map(([label, val, cls], i) => (
            <div key={i} className="card-sharp p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
              <div className={`text-lg font-bold mt-1 font-mono ${cls}`}>
                {typeof val === "number" && Math.abs(val) >= 1e5 ? fmtShort(val) : val}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assumptions */}
      <div className="card-sharp p-4 space-y-3" data-testid="wc-assumptions">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Assumptions for projections</div>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <Switch checked={useDefaults} onCheckedChange={setUseDefaults} data-testid="wc-use-defaults" />
            Use historical defaults
          </label>
        </div>
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${useDefaults ? "opacity-40 pointer-events-none" : ""}`}>
          {[
            ["revenue_growth_pct", "Revenue growth % (annual)", historical?.gross_margin_pct ? 8 : null],
            ["gross_margin_pct", "Gross margin %", historical?.gross_margin_pct],
            ["ar_days", "AR days", historical?.ar_days],
            ["ap_days", "AP days", historical?.ap_days],
            ["inv_days", "Inventory days", historical?.inv_days],
            ["opex_pct_of_revenue", "Opex % of revenue", null],
            ["starting_bank", "Starting bank (₹)", historical?.bank_close],
          ].map(([key, label, placeholder]) => (
            <div key={key}>
              <Label className="text-[10px] uppercase font-mono">{label}</Label>
              <Input type="number" step="0.1"
                value={assumptions[key] ?? ""}
                onChange={e => set({ [key]: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder={placeholder != null ? `default ${placeholder}` : ""}
                className="h-8 text-sm mt-1 text-right tabular-nums font-mono"
                data-testid={`wc-assume-${key}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-1">
          <Button onClick={runSim} disabled={loading} variant="outline" className="rounded-sm gap-1.5" data-testid="wc-rerun">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-run simulation
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      {sim && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="wc-summary">
          <div className="card-sharp p-4 border-l-4 border-l-red-500 bg-red-500/5">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Peak WC gap</div>
            <div className="text-xl font-bold mt-1 font-mono text-red-500">{fmtShort(sim.peak_wc_gap)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">month #{sim.peak_wc_gap_month}</div>
          </div>
          <div className="card-sharp p-4 border-l-4 border-l-emerald-500 bg-emerald-500/5">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Recommended bank facility</div>
            <div className="text-xl font-bold mt-1 font-mono text-emerald-600">{fmtShort(sim.recommended_sanction_limit)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">75% of peak WC gap</div>
          </div>
          <div className={`card-sharp p-4 border-l-4 ${sim.lowest_projected_bank < 0 ? "border-l-red-500 bg-red-500/5" : "border-l-emerald-500 bg-emerald-500/5"}`}>
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Lowest bank</div>
            <div className={`text-xl font-bold mt-1 font-mono ${sim.lowest_projected_bank < 0 ? "text-red-500" : "text-emerald-600"}`}>{fmtShort(sim.lowest_projected_bank)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">across horizon</div>
          </div>
          <div className="card-sharp p-4">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">Applied growth (annual)</div>
            <div className="text-xl font-bold mt-1 font-mono">{sim.assumptions_used?.revenue_growth_pct}%</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">margin {sim.assumptions_used?.gross_margin_pct}%</div>
          </div>
        </div>
      )}

      {/* Projections table */}
      {sim && (
        <div className="card-sharp overflow-hidden">
          <div className="p-3 border-b bg-muted/30 text-sm font-semibold">Month-wise projections</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left p-2">Month</th>
                  <th className="text-right p-2">Revenue</th>
                  <th className="text-right p-2">Gross Profit</th>
                  <th className="text-right p-2">PBT</th>
                  <th className="text-right p-2">AR</th>
                  <th className="text-right p-2">AP</th>
                  <th className="text-right p-2">Inventory</th>
                  <th className="text-right p-2">WC Gap</th>
                  <th className="text-right p-2">Bank</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sim.projections.map((r, i) => (
                  <tr key={i} className={`hover:bg-muted/30 ${r.bank_projected < 0 ? "bg-red-500/5" : ""}`}>
                    <td className="p-2 font-mono font-semibold">M{r.month}</td>
                    <td className="p-2 text-right font-mono">{fmtShort(r.revenue)}</td>
                    <td className="p-2 text-right font-mono text-emerald-600">{fmtShort(r.gross_profit)}</td>
                    <td className={`p-2 text-right font-mono ${r.net_profit_before_tax < 0 ? "text-red-500" : "text-emerald-600"}`}>{fmtShort(r.net_profit_before_tax)}</td>
                    <td className="p-2 text-right font-mono">{fmtShort(r.ar)}</td>
                    <td className="p-2 text-right font-mono">{fmtShort(r.ap)}</td>
                    <td className="p-2 text-right font-mono">{fmtShort(r.inventory)}</td>
                    <td className="p-2 text-right font-mono font-semibold text-red-500">{fmtShort(r.working_capital_gap)}</td>
                    <td className={`p-2 text-right font-mono font-semibold ${r.bank_projected < 0 ? "text-red-600" : "text-blue-600"}`}>{fmtShort(r.bank_projected)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
